import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import i18n from '../../i18n'
import { invalidateQueries, updateToast } from '../helpers'
import { getAgentById, getAgentPermissions, getAgents, reassignAgent, updateAgent, updateCurrentAgentSettings } from './functions'
import { Agent, AgentPermission, ReassignAgentPayload, UpdateAgentPayload, UpdateCurrentAgentSettingsPayload } from './types'

const agents = {
    GetAgents: {
        useQuery: (options?: Omit<UseQueryOptions<Agent[], AxiosError>, 'queryKey' | 'queryFn'>) => {
            return useQuery({
                queryKey: ['agents', 'list'],
                queryFn: getAgents,
                staleTime: 1 * 60 * 1000,
                ...options,
            })
        },
    },
    GetAgent: {
        useQuery: (id: string | number, options?: Omit<UseQueryOptions<Agent, AxiosError>, 'queryKey' | 'queryFn'>) => {
            return useQuery({
                queryKey: ['agents', id],
                queryFn: () => getAgentById(id),
                staleTime: 1 * 60 * 1000,
                enabled: Boolean(id),
                ...options,
            })
        },
    },
    UpdateAgent: {
        useMutation: (options?: UseMutationOptions<Agent, AxiosError, UpdateAgentPayload>) => {
            return useMutation({
                onMutate: () => toast.loading(i18n.t('toast.loading') as string),
                mutationFn: (payload: UpdateAgentPayload) => updateAgent(payload),
                onSuccess: (data, variables, context) => {
                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.update.success') as string, 'success')

                    invalidateQueries(['currentAgent'])
                    invalidateQueries(['properties', 'list'])
                    invalidateQueries(['projects', 'list'])
                    invalidateQueries(['agents', 'list'])
                    invalidateQueries(['agents', variables.agentId.toString()])
                },
                onError: (error, variables, context) => {
                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.update.error') as string, 'error')
                },
                ...options,
            })
        },
    },
    UpdateCurrentAgentSettings: {
        useMutation: (options?: UseMutationOptions<void, AxiosError, UpdateCurrentAgentSettingsPayload>) => {
            return useMutation({
                mutationFn: (payload: UpdateCurrentAgentSettingsPayload) => updateCurrentAgentSettings(payload),
                onSuccess: () => invalidateQueries(['currentAgent']),
                ...options,
            })
        },
    },
    ReassignAgent: {
        useMutation: (options?: UseMutationOptions<void, AxiosError, ReassignAgentPayload>) => {
            return useMutation({
                onMutate: () => toast.loading(i18n.t('toast.loading.agent') as string),
                mutationFn: (payload: ReassignAgentPayload) => reassignAgent(payload),
                onSuccess: (data, variables, context) => {
                    const { dataKey, ids } = variables
                    ids.forEach((id) => invalidateQueries([dataKey, id.toString()]))
                    invalidateQueries([dataKey, 'list'])

                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.loading.agent.success') as string, 'success')
                },
                onError: (error, variables, context) => {
                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.loading.agent.error') as string, 'error')
                },
                ...options,
            })
        },
    },
    GetAgentPermissions: {
        useQuery: (options?: Omit<UseQueryOptions<AgentPermission[], AxiosError>, 'queryKey' | 'queryFn'>) => {
            return useQuery({
                queryKey: ['agents', 'permissions'],
                queryFn: getAgentPermissions,
                staleTime: 5 * 60 * 1000,
                ...options,
            })
        },
    },
}

export default agents
